import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Sparkles, Settings, Play, RefreshCw, CheckCircle, 
  AlertCircle, ChevronRight, Loader2, Workflow, MessageSquare,
  Clock, Users, Building2, Zap, Save, FileText, Phone
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import { useAuth, API } from '../../App';
import IntakeTemplateBuilder from '../../components/IntakeTemplateBuilder';
import axios from 'axios';
import { toast } from 'sonner';

import ChannelSelector from '../../components/ChannelSelector';

const AISetupPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [workflowSuggestions, setWorkflowSuggestions] = useState([]);
  const [generatingSuggestions, setGeneratingSuggestions] = useState(false);
  
  // Channel state for multi-channel AI profile
  const [selectedChannelId, setSelectedChannelId] = useState(null);
  const [channels, setChannels] = useState([]);
  
  // Business Profile state
  const [businessProfile, setBusinessProfile] = useState('');
  const [aiEnabled, setAiEnabled] = useState(true);
  const [humanInLoop, setHumanInLoop] = useState(true);
  const [configOverrides, setConfigOverrides] = useState({});
  
  const isOwner = user?.role === 'owner' || user?.is_super_admin;
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchOnboardingStatus();
    // Auto-select channel for admin or fetch accessible channels
    fetchInitialChannel();
  }, []);
  
  // Reload AI profile when channel changes
  useEffect(() => {
    if (selectedChannelId) {
      fetchAiProfile(selectedChannelId);
    }
  }, [selectedChannelId]);

  const fetchInitialChannel = async () => {
    try {
      const response = await axios.get(`${API}/channels/accessible`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const fetchedChannels = response.data.channels || [];
      setChannels(fetchedChannels);
      
      if (fetchedChannels.length > 0) {
        setSelectedChannelId(fetchedChannels[0].id);
      } else {
        fetchAiProfile(null);
      }
    } catch (error) {
      console.error('Failed to fetch channels:', error);
      setChannels([]);
      fetchAiProfile(null);
    }
  };

  const fetchOnboardingStatus = async () => {
    try {
      const response = await axios.get(`${API}/onboarding/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOnboardingStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch onboarding status:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied. Only Owner and Admin can access AI Setup.');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const fetchAiProfile = async (channelId) => {
    try {
      let url;
      if (channelId) {
        url = `${API}/channels/${channelId}/ai-config`;
      } else {
        url = `${API}/settings/ai-profile`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusinessProfile(response.data.business_profile || '');
      setAiEnabled(response.data.ai_enabled ?? true);
      setHumanInLoop(response.data.human_in_loop ?? true);
      setConfigOverrides(response.data.overrides || {});
    } catch (error) {
      console.error('Failed to fetch AI profile:', error);
    }
  };
  
  const handleSaveBusinessProfile = async () => {
    setSaving(true);
    try {
      const payload = {
        business_profile: businessProfile,
        ai_enabled: aiEnabled,
        human_in_loop: humanInLoop
      };
      
      if (selectedChannelId) {
        // Save channel-level config
        await axios.put(`${API}/channels/${selectedChannelId}/ai-config`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Channel AI profile saved');
      } else {
        // Save org-level config (fallback)
        await axios.put(`${API}/settings/ai-profile`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Organization AI profile saved');
      }
    } catch (error) {
      toast.error('Failed to save business profile');
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerateSuggestions = async () => {
    if (!onboardingStatus?.industry) {
      toast.error('Please complete onboarding first to get workflow suggestions');
      return;
    }

    setGeneratingSuggestions(true);
    try {
      const response = await axios.post(`${API}/onboarding/suggest-workflows`, {
        industry: onboardingStatus.industry,
        questionnaire_answers: onboardingStatus.onboarding_summary?.answers || {}
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkflowSuggestions(response.data.suggestions || []);
      toast.success('Workflow suggestions generated!');
    } catch (error) {
      toast.error('Failed to generate suggestions');
    } finally {
      setGeneratingSuggestions(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  // Check if user has access (not operator)
  if (user?.role === 'operator') {
    return (
      <div className="max-w-4xl mx-auto">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            AI Setup is only available to Organization Owners and Admins.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const isOnboarded = onboardingStatus?.onboarding_completed;
  const summary = onboardingStatus?.onboarding_summary;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-emerald-600" />
            AI Setup
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure your AI automation settings
          </p>
        </div>
        {!isOnboarded && (
          <Button 
            onClick={() => navigate('/dashboard/onboarding')}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="start-setup-btn"
          >
            <Play className="w-4 h-4 mr-2" />
            Start Smart Setup
          </Button>
        )}
      </div>

      {/* AI Status Card - Shows active/configured state */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {aiEnabled ? (
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            ) : (
              <AlertCircle className="w-5 h-5 text-amber-500" />
            )}
            AI Configuration Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* AI Active Indicator */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${aiEnabled ? 'bg-emerald-100' : 'bg-gray-100'}`}>
                  <Sparkles className={`w-5 h-5 ${aiEnabled ? 'text-emerald-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <p className="font-medium">AI Automation</p>
                  <p className="text-sm text-muted-foreground">
                    {aiEnabled ? 'AI is actively processing messages' : 'AI is disabled'}
                  </p>
                </div>
              </div>
              <Badge className={aiEnabled ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'}>
                {aiEnabled ? 'Active' : 'Inactive'}
              </Badge>
            </div>

            {/* HITL Status */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${humanInLoop !== undefined ? 'bg-blue-100' : 'bg-amber-100'}`}>
                  <Users className={`w-5 h-5 ${humanInLoop !== undefined ? 'text-blue-600' : 'text-amber-500'}`} />
                </div>
                <div>
                  <p className="font-medium">Human Approval (HITL)</p>
                  <p className="text-sm text-muted-foreground">
                    {humanInLoop ? 'AI drafts require approval before sending' : 'AI sends replies automatically'}
                  </p>
                </div>
              </div>
              <Badge className={humanInLoop ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}>
                {humanInLoop ? 'Review Mode' : 'Auto-Send'}
              </Badge>
            </div>

            {/* Status Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <div className="p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Building2 className="w-4 h-4" />
                  Industry
                </div>
                <p className="font-medium mt-1 capitalize">{onboardingStatus?.industry?.replace('_', ' ') || 'Not set'}</p>
              </div>
              
              <div className="p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Workflow className="w-4 h-4" />
                  Workflows
                </div>
                <p className="font-medium mt-1">{summary?.workflows_suggested || 0} created</p>
              </div>
              
              <div className="p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Zap className="w-4 h-4" />
                  AI Mode
                </div>
                <p className="font-medium mt-1">
                  {aiEnabled ? 'Enabled' : 'Disabled'}
                </p>
              </div>
              
              <div className="p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="w-4 h-4" />
                  Availability
                </div>
                <p className="font-medium mt-1">24/7</p>
              </div>
            </div>

            {/* Action Buttons */}
            {isOnboarded ? (
              <div className="flex gap-2 mt-4">
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/dashboard/onboarding?rerun=true')}
                  data-testid="rerun-setup-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Re-run Setup
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/dashboard/settings')}
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Advanced Settings
                </Button>
              </div>
            ) : (
              <div className="text-center py-4 border-t mt-4">
                <p className="text-muted-foreground mb-3">
                  Complete the smart setup wizard to configure AI automation
                </p>
                <Button 
                  onClick={() => navigate('/dashboard/onboarding')}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Start Setup (5 minutes)
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Workflow Suggestions Section */}
      {isOnboarded && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Workflow className="w-5 h-5" />
                  Workflow Suggestions
                </CardTitle>
                <CardDescription>
                  AI-generated workflow recommendations for your business
                </CardDescription>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleRegenerateSuggestions}
                disabled={generatingSuggestions}
                data-testid="regenerate-suggestions-btn"
              >
                {generatingSuggestions ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                <span className="ml-2">Regenerate</span>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {workflowSuggestions.length > 0 ? (
              <div className="space-y-3">
                {workflowSuggestions.map((suggestion, index) => (
                  <div 
                    key={suggestion.id || index}
                    className="p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium">{suggestion.name}</h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          {suggestion.description}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">
                            {suggestion.trigger?.type?.replace('_', ' ')}
                          </Badge>
                          {suggestion.is_ai_suggested && (
                            <Badge variant="secondary" className="text-xs">
                              <Sparkles className="w-3 h-3 mr-1" />
                              AI Suggested
                            </Badge>
                          )}
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => navigate('/dashboard/workflows')}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <Workflow className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>Click "Regenerate" to get AI-powered workflow suggestions</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Business Profile Section - REQUIRED FOR AI */}
      <Card data-testid="business-profile-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Business Profile
              </CardTitle>
              <CardDescription>
                Configure AI business context per channel. Each channel can have its own business profile.
              </CardDescription>
            </div>
          </div>
          
          {/* Channel Selector */}
          <div className="mt-3" data-testid="ai-channel-selector">
            <ChannelSelector
              value={selectedChannelId}
              onChange={(channelId) => setSelectedChannelId(channelId)}
              showAllOption={false}
            />
            {configOverrides && Object.values(configOverrides).some(v => v) && (
              <p className="text-xs text-blue-600 mt-1">
                This channel has custom overrides. Fields not overridden inherit from organization defaults.
              </p>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="businessProfile">Describe your business *</Label>
            <Textarea
              id="businessProfile"
              placeholder="Tell us about your business. Include details like:
• What type of business are you? (e.g., dental clinic, restaurant, real estate agency)
• Where are you located? (City/Country)
• What services do you offer?
• What are your operating hours?
• Any special policies or information customers should know?"
              value={businessProfile}
              onChange={(e) => setBusinessProfile(e.target.value)}
              rows={6}
              className="resize-none"
              data-testid="business-profile-textarea"
            />
            <p className="text-xs text-muted-foreground">
              This helps the AI understand your business context and provide more accurate responses.
              {businessProfile.toLowerCase().includes('malaysia') && (
                <span className="block text-emerald-600 mt-1">
                  ✓ Timezone auto-detected: Asia/Kuala_Lumpur
                </span>
              )}
            </p>
          </div>
          
          <div className="p-4 bg-muted/50 rounded-lg">
            <h4 className="font-medium text-sm mb-2">Helper Questions:</h4>
            <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
              <li>What type of business are you?</li>
              <li>Where are you located? (City/Country)</li>
              <li>What services do you offer?</li>
              <li>What are your operating hours?</li>
              <li>Any special policies?</li>
            </ul>
          </div>
          
          <Button 
            onClick={handleSaveBusinessProfile}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="save-business-profile-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Business Profile
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* WhatsApp Setup Guide */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-green-600" />
            WhatsApp Connection Guide
          </CardTitle>
          <CardDescription>
            Step-by-step instructions to connect your WhatsApp Business
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="border-l-2 border-emerald-600 pl-4 space-y-4">
              {[
                { step: 1, title: 'Create Meta Developer Account', desc: 'Go to developers.facebook.com and create a developer account' },
                { step: 2, title: 'Create a Meta App', desc: 'Create a new app with "Business" type in the Meta Developer Console' },
                { step: 3, title: 'Add WhatsApp Product', desc: 'Add WhatsApp to your app and complete the setup' },
                { step: 4, title: 'Generate Access Token', desc: 'Create a permanent access token for API access' },
                { step: 5, title: 'Get Phone Number ID', desc: 'Copy your Phone Number ID from the WhatsApp settings' },
                { step: 6, title: 'Connect in Flowteras', desc: 'Paste your credentials in Settings → WhatsApp' },
              ].map((item) => (
                <div key={item.step} className="flex gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center text-sm font-medium">
                    {item.step}
                  </div>
                  <div>
                    <p className="font-medium">{item.title}</p>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <Button 
              variant="outline" 
              className="w-full mt-4"
              onClick={() => navigate('/dashboard/settings')}
            >
              Go to WhatsApp Settings
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Structured Intake Templates - Multi-Channel Support */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-purple-600" />
            Structured Intake
          </CardTitle>
          <CardDescription>
            Configure what information AI should collect before escalating conversations
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Channel Selector for Intake Templates */}
          {channels.length > 1 && isOwner && (
            <div className="mb-6 flex items-center gap-3">
              <Label className="text-sm text-gray-600">Configure templates for:</Label>
              <Select 
                value={selectedChannelId || ''} 
                onValueChange={setSelectedChannelId}
              >
                <SelectTrigger className="w-[200px]">
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-green-500" />
                    <SelectValue placeholder="Select channel" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {channels.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.display_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Admin sees only their channel */}
          {isAdmin && user?.assigned_channel_id && (
            <div className="mb-4 flex items-center gap-2 text-sm text-gray-600">
              <Phone className="h-4 w-4 text-blue-500" />
              Showing templates for your assigned channel
            </div>
          )}

          {/* Intake Template Builder */}
          {selectedChannelId ? (
            <IntakeTemplateBuilder 
              channelId={selectedChannelId}
              channels={channels}
            />
          ) : channels.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Phone className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p>Add a WhatsApp channel first to configure intake templates</p>
              <Button 
                variant="outline" 
                className="mt-3"
                onClick={() => navigate('/dashboard/channels')}
              >
                Go to Channels
              </Button>
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500">
              <RefreshCw className="h-6 w-6 animate-spin mx-auto" />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AISetupPage;
